// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-SCENE-QUERY — the route-projector registration entry point
// ═══════════════════════════════════════════════════════════════════════════
//
// `registerSceneQueryProjectors()` is the EXPLICIT registration call S7 invokes ONCE at mount
// to wire the SCENE-QUERY route bodies into the canonical `sceneSummaryProjector` registry.
// PR-A registers `entities`; PR-B registers the 3 document-level table routes (layers / blocks /
// spaces); PR-C registers the 3 viewport routes (viewports / viewport-frozen-layers /
// viewport-layer-overrides) — completing all 9 routes. T8 adds a 10th: `osnap-query` (nearest
// endpoint/midpoint/intersection to a point) — a read-only informational route, NOT an
// `aiCallable` action.
//
// NOT a module-eval SIDE EFFECT (memory `feedback-module-eval-side-effects-need-broad-test-scope`):
// merely IMPORTING this module registers NOTHING — only CALLING `registerSceneQueryProjectors()`
// mutates the registry. So a test that imports `projectEntities` (or this barrel) does not
// silently alter the global projector registry; it opts in explicitly + restores via
// `unregisterRouteProjector` in cleanup.
//
// LEAF purity (ПРИОРИТЕТ #2): imports ONLY the seam + the sibling body — NO editorStore /
// agentBridge / command-engine. `npm run deps:graph` cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import { registerRouteProjector } from "../sceneSummaryProjector";
import { projectBlocks } from "./projectBlocks";
import { projectEntities } from "./projectEntities";
import { projectLayers } from "./projectLayers";
import { projectOsnapQuery } from "./osnapQuery";
import { projectSpaces } from "./projectSpaces";
import { projectViewportFrozenLayers } from "./projectViewportFrozenLayers";
import { projectViewportLayerOverrides } from "./projectViewportLayerOverrides";
import { projectViewports } from "./projectViewports";

/**
 * Register every SCENE-QUERY route projector into the canonical `sceneSummaryProjector` registry.
 * Idempotent (a re-register overwrites the prior body — `registerRouteProjector` is
 * overwrite-safe). S7 calls this ONCE at agent-bridge mount; a test calls it then restores the
 * registry via `unregisterRouteProjector(route)` in its cleanup. PR-C completed the registration
 * with the 3 viewport routes (9 `state.read` routes); T8 adds the 10th (`osnap-query`).
 */
export function registerSceneQueryProjectors(): void {
  registerRouteProjector("entities", projectEntities);
  // PR-B — the 3 document-level table routes (NOT space-scoped).
  registerRouteProjector("layers", projectLayers);
  registerRouteProjector("blocks", projectBlocks);
  registerRouteProjector("spaces", projectSpaces);
  // PR-C — the 3 viewport routes (the `viewports` route is space-scoped; the 2 nested drill-downs
  // are keyed by `?viewportEntityId`).
  registerRouteProjector("viewports", projectViewports);
  registerRouteProjector("viewport-frozen-layers", projectViewportFrozenLayers);
  registerRouteProjector("viewport-layer-overrides", projectViewportLayerOverrides);
  // T8 — the nearest endpoint/midpoint/intersection-to-a-point read-only route.
  registerRouteProjector("osnap-query", projectOsnapQuery);
}

// Barrel re-export of the bodies so a consumer / a test can register a probe / inspect the fn
// without reaching into the module path.
export { projectEntities } from "./projectEntities";
export { projectLayers } from "./projectLayers";
export { projectBlocks } from "./projectBlocks";
export { projectSpaces } from "./projectSpaces";
export { projectViewports } from "./projectViewports";
export { projectViewportFrozenLayers } from "./projectViewportFrozenLayers";
export { projectViewportLayerOverrides } from "./projectViewportLayerOverrides";
export { projectOsnapQuery } from "./osnapQuery";
